import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { fetchZoneMappings } from '../services/googleSheets.ts';

interface ZoneSettingsProps {
    isOpen: boolean;
    onClose: () => void;
    onZoneSelect: (zone: string, sheetId: string, governorate: string) => void;
    currentZone: string;
    currentGovernorate: string;
    isAutomatic: boolean;
    onAutomaticChange: (isAutomatic: boolean) => void;
}

interface ZoneMapping {
    zone: string;
    sheetUrl: string;
    governorate: string;
}

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    padding: 2rem;
    border-radius: 15px;
    width: 90%;
    max-width: 500px;
    max-height: 80vh;
    overflow-y: auto;
    position: relative;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 1rem;
    left: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #666;
    
    &:hover {
        color: #333;
    }
`;

const Title = styled.h2`
    text-align: center;
    margin-bottom: 1.5rem;
    color: #333;
`;

const ToggleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 2rem;
`;

const ToggleButton = styled.button<{ active: boolean }>`
    padding: 0.5rem 1rem;
    border: 2px solid ${props => props.active ? '#00BFA6' : '#ddd'};
    background: ${props => props.active ? '#00BFA6' : 'white'};
    color: ${props => props.active ? 'white' : '#666'};
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
        border-color: #00BFA6;
        color: ${props => props.active ? 'white' : '#00BFA6'};
    }
`;

const ZoneList = styled.div`
    display: grid;
    gap: 1rem;
`;

const GovernorateSelect = styled.select`
    width: 100%;
    padding: 0.8rem;
    border: 2px solid #eee;
    border-radius: 10px;
    margin-bottom: 1rem;
    direction: rtl;
    text-align: right;
    font-size: 1rem;
    color: #333;
    background-color: white;
    
    &:focus {
        border-color: #00BFA6;
        outline: none;
    }
`;

const ZoneButton = styled.button<{ active: boolean }>`
    padding: 1rem;
    border: 2px solid ${props => props.active ? '#00BFA6' : '#eee'};
    background: ${props => props.active ? '#f0fff4' : 'white'};
    border-radius: 10px;
    text-align: right;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
        border-color: #00BFA6;
        background: #f0fff4;
    }
`;

const ZoneSettings: React.FC<ZoneSettingsProps> = ({
    isOpen,
    onClose,
    onZoneSelect,
    currentZone,
    currentGovernorate,
    isAutomatic,
    onAutomaticChange
}) => {
    const [zones, setZones] = useState<ZoneMapping[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedGovernorate, setSelectedGovernorate] = useState(currentGovernorate || '');
    const [governorates, setGovernorates] = useState<string[]>([]);

    useEffect(() => {
        const loadZones = async () => {
            try {
                const zoneMappings = await fetchZoneMappings();
                setZones(zoneMappings);
                
                // استخراج قائمة المحافظات الفريدة
                const uniqueGovernorates = [...new Set(zoneMappings.map(zone => zone.governorate))].filter(Boolean);
                setGovernorates(uniqueGovernorates);
                
                // إذا لم يكن هناك محافظة محددة ولكن هناك محافظات متاحة
                if (!selectedGovernorate && uniqueGovernorates.length > 0) {
                    setSelectedGovernorate(uniqueGovernorates[0]);
                }
            } catch (error) {
                console.error('Error loading zones:', error);
                toast.error('حدث خطأ في تحميل المناطق');
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            loadZones();
        }
    }, [isOpen, selectedGovernorate]);

    const handleGovernorateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedGovernorate(e.target.value);
    };

    // الحصول على مناطق المحافظة المحددة
    const filteredZones = selectedGovernorate 
        ? zones.filter(zone => zone.governorate === selectedGovernorate)
        : zones;

    if (!isOpen) return null;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={e => e.stopPropagation()}>
                <CloseButton onClick={onClose}>×</CloseButton>
                <Title>إعدادات المنطقة</Title>
                
                <ToggleContainer>
                    <ToggleButton
                        active={isAutomatic}
                        onClick={() => onAutomaticChange(true)}
                    >
                        تلقائي
                    </ToggleButton>
                    <ToggleButton
                        active={!isAutomatic}
                        onClick={() => onAutomaticChange(false)}
                    >
                        يدوي
                    </ToggleButton>
                </ToggleContainer>

                {!isAutomatic && (
                    <>
                        <GovernorateSelect 
                            value={selectedGovernorate} 
                            onChange={handleGovernorateChange}
                        >
                            {governorates.map(gov => (
                                <option key={gov} value={gov}>
                                    {gov}
                                </option>
                            ))}
                        </GovernorateSelect>
                        
                        <ZoneList>
                            {loading ? (
                                <div>جاري تحميل المناطق...</div>
                            ) : (
                                filteredZones.map((zone) => (
                                    <ZoneButton
                                        key={zone.zone}
                                        active={zone.zone === currentZone}
                                        onClick={() => {
                                            const sheetId = zone.sheetUrl.split('/')[5];
                                            onZoneSelect(zone.zone, sheetId, zone.governorate);
                                            onClose();
                                        }}
                                    >
                                        {zone.zone}
                                    </ZoneButton>
                                ))
                            )}
                        </ZoneList>
                    </>
                )}
            </ModalContent>
        </ModalOverlay>
    );
};

export default ZoneSettings; 